import "./ExportSuccessChart.css";

/**
 * Phase 18 WU-18.E2 — Export success per destination.
 *
 * Dumb presentational component. Renders one horizontal stacked bar per
 * destination (succeeded + failed + pending) with success-rate % on the right.
 */
export interface ExportSuccessRow {
  destination: string;
  total: number;
  succeeded: number;
  failed: number;
  pending: number;
  successRate: number;
}

export interface ExportSuccessChartProps {
  rows: ExportSuccessRow[];
}

function pct(n: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((n / total) * 100);
}

export function ExportSuccessChart({ rows }: ExportSuccessChartProps) {
  if (rows.length === 0) {
    return (
      <div className="export-success__empty" data-testid="export-success-empty">
        No exports in this period.
      </div>
    );
  }

  const sorted = rows.slice().sort((a, b) => b.total - a.total);

  return (
    <div className="export-success" data-testid="export-success">
      {sorted.map((row) => {
        const succPct = pct(row.succeeded, row.total);
        const failPct = pct(row.failed, row.total);
        const pendPct = Math.max(0, 100 - succPct - failPct);
        return (
          <div
            key={row.destination}
            className="export-success__row"
            data-testid={`export-success-row-${row.destination}`}
          >
            <div className="export-success__label">{row.destination}</div>
            <div className="export-success__bar">
              <div
                className="export-success__seg export-success__seg--succeeded"
                style={{ width: `${succPct}%` }}
                title={`${row.succeeded} succeeded`}
              />
              <div
                className="export-success__seg export-success__seg--failed"
                style={{ width: `${failPct}%` }}
                title={`${row.failed} failed`}
              />
              <div
                className="export-success__seg export-success__seg--pending"
                style={{ width: `${pendPct}%` }}
                title={`${row.pending} pending`}
              />
            </div>
            <div className="export-success__rate">
              {(row.successRate * 100).toFixed(1)}%
            </div>
          </div>
        );
      })}
    </div>
  );
}
